import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getRuntime } from '../utils/runtime';
import { useGamification } from '../hooks/GamificationContext';
import { fetchActiveEvent } from '../api/eventsApi';
import { prepareImageUpload } from '../utils/photoUpload';
import { isChallengeFocusEnabled } from '../utils/settings';
import { buildFundSharePayload, getFundSharingFlags, shareFund } from '../utils/fundSharing';
import { XpToast } from './XpToast';
import einwegpfandIcon from '../assets/quick-mark/quickmark-einwegpfand.png';
import mehrwegpfandIcon from '../assets/quick-mark/quickmark-mehrwegpfand.png';
import gemischtesPfandIcon from '../assets/quick-mark/quickmark-gemischtes-pfand.png';
import zuVerschenkenIcon from '../assets/quick-mark/quickmark-zu-verschenken.png';
import '../styles/quick-mark.css';
import { emitDrinkDiscoveryChanges } from '../utils/drinkDiscovery';

const QUICK_POSITION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 30000,
};

const MARK_OPTIONS = [
  {
    key: 'einwegpfand',
    itemType: 'pfand',
    pfandType: 'einweg',
    amountType: 'einwegpfand',
    image: einwegpfandIcon,
    imageAlt: 'Einwegdose und Einweg-Plastikflasche mit DPG-Markierung',
    title: 'Einwegpfand melden',
    text: 'Dose oder PET-Flasche mit DPG-Einwegpfand.',
    className: 'quick-mark-type-button--einweg',
  },
  {
    key: 'mehrwegpfand',
    itemType: 'pfand',
    pfandType: 'mehrweg',
    amountType: 'mehrwegpfand',
    image: mehrwegpfandIcon,
    imageAlt: 'Mehrwegflasche',
    title: 'Mehrwegpfand melden',
    text: 'Mehrwegflasche aus Glas oder PET.',
    className: 'quick-mark-type-button--mehrweg',
  },
  {
    key: 'gemischtes-pfand',
    itemType: 'pfand',
    pfandType: 'gemischt',
    amountType: 'gemischtes_pfand',
    image: gemischtesPfandIcon,
    imageAlt: 'Einwegdose, Einweg-Plastikflasche und Mehrwegflasche',
    title: 'Gemischtes Pfand melden',
    text: 'Einweg und Mehrweg liegen zusammen oder mehrere Gebinde gemischt.',
    className: 'quick-mark-type-button--bulk',
  },
  {
    key: 'zu-verschenken',
    itemType: 'giveaway',
    pfandType: 'zu_verschenken',
    amountType: 'giveaway',
    image: zuVerschenkenIcon,
    imageAlt: 'Kiste mit Dingen zu verschenken',
    title: 'Zu verschenken melden',
    text: 'Kostenlose Sachen am Wegesrand, keine Pfandmeldung.',
    className: 'quick-mark-type-button--giveaway',
  },
];

function resolveSource(explicitSource, runtimeSource, standalone) {
  const raw = String(explicitSource || runtimeSource || '').trim().toLowerCase();
  if (raw === 'sticker' || raw === 'homepage' || raw === 'app' || raw === 'public_page') {
    return raw;
  }
  return standalone ? 'homepage' : 'app';
}

function getSourceLabel(source) {
  switch (source) {
    case 'sticker':
      return 'Sticker-QR';
    case 'public_page':
      return 'öffentliche Markierungsseite';
    case 'homepage':
      return 'Startseite';
    case 'app':
    default:
      return 'App';
  }
}

function getTypeLabel(type) {
  if (type === 'zu_verschenken') {
    return 'Zu verschenken';
  }
  if (type === 'gemischt') {
    return 'Gemischt';
  }

  return type === 'mehrweg' ? 'Mehrweg' : 'Einweg';
}

function getAmountLabel(type) {
  switch (type) {
    case 'einwegpfand':
      return 'Einwegpfand';
    case 'mehrwegpfand':
      return 'Mehrwegpfand';
    case 'gemischtes_pfand':
      return 'Gemischtes Pfand';
    case 'giveaway':
      return 'Fundsache / zu verschenken';
    case 'multiple':
      return 'Mehrere Teile';
    case 'bag':
      return 'Tüte / größere Menge';
    case 'crate':
      return 'Kasten / Mehrweg-Menge';
    case 'single':
    default:
      return 'Einzelnes Teil';
  }
}

function FundShareCard({ payload, flags, onShare, shareState, onSkip }) {
  if (!flags.socialSharingEnabled || !flags.publicFundShareEnabled || !payload?.url) {
    return null;
  }

  const channels = [
    flags.shareWhatsAppEnabled ? { key: 'whatsapp', label: 'WhatsApp' } : null,
    flags.shareInstagramEnabled ? { key: 'instagram', label: 'Instagram' } : null,
    flags.shareFacebookEnabled ? { key: 'facebook', label: 'Facebook' } : null,
    flags.shareEmailEnabled ? { key: 'email', label: 'E-Mail' } : null,
    { key: 'copy', label: 'Link kopieren' },
  ].filter(Boolean);

  return (
    <div className="quick-mark-share-card">
      <div className="quick-mark-share-card__head">
        <div>
          <strong>Beute gesichert!</strong>
          <span>Dein Fund ist auf der Karte. Teile ihn freiwillig mit deiner Crew oder mach direkt weiter.</span>
        </div>
        <div className="quick-mark-share-card__value">{payload.estimatedValue}</div>
      </div>
      <div className="quick-mark-share-card__meta">
        <span>{payload.fundType}</span>
        <span>{payload.locationLabel}</span>
      </div>
      <div className="quick-mark-share-card__actions">
        {channels.map(channel => (
          <button
            key={channel.key}
            type="button"
            className="quick-mark-share-button"
            onClick={() => onShare(channel.key)}
            disabled={shareState.busy === channel.key}
          >
            {shareState.busy === channel.key ? 'Wird geöffnet …' : channel.label}
          </button>
        ))}
        <button type="button" className="quick-mark-share-button quick-mark-share-button--skip" onClick={onSkip}>
          Nicht teilen
        </button>
      </div>
      {shareState.message ? (
        <div className={`quick-mark-share-message ${shareState.type === 'error' ? 'is-error' : 'is-success'}`}>
          {shareState.message}
        </div>
      ) : null}
    </div>
  );
}

function getRootDatasetSource() {
  if (typeof document === 'undefined') return '';
  return document.getElementById('pfandpirat-root')?.dataset?.quickMarkSource || '';
}

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.matchMedia?.('(display-mode: fullscreen)')?.matches
    || window.navigator?.standalone
  );
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

function getInstallHint() {
  if (isIosDevice()) {
    return 'Im Teilen-Menue die Option "Zum Home-Bildschirm" waehlen.';
  }

  return 'Im Browser-Menue "App installieren" oder "Zum Startbildschirm hinzufuegen" waehlen.';
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Dein Browser unterstützt keine Standortfreigabe.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
        });
      },
      (error) => {
        if (error?.code === 1) {
          reject(new Error('Ohne Standortfreigabe kann der Fund nicht markiert werden.'));
          return;
        }
        reject(new Error('Der Standort konnte gerade nicht ermittelt werden. Bitte versuche es erneut.'));
      },
      QUICK_POSITION_OPTIONS
    );
  });
}

export function QuickMarkPage({ standalone = false, source: sourceProp = '', selectedDrink = null }) {
  const runtime = useMemo(() => getRuntime(), []);
  const gamification = useGamification();
  const localToastTimerRef = useRef(null);
  const source = useMemo(
    () => resolveSource(sourceProp || getRootDatasetSource(), runtime?.quickMarkSource, standalone),
    [runtime, sourceProp, standalone]
  );

  const logoUrl = runtime?.quickMarkLogoUrl || runtime?.logoUrl || 'https://pfandpirat.com/wp-content/uploads/2025/05/cropped-Pfandpirat-300x300-1-125x125.png';
  const apiUrl = runtime?.publicQuickMarkApiUrl || '/wp-json/pfandpirat/v1/pfandspenden/quick-mark';
  const appUrl = runtime?.appUrl || '/app/';
  const websiteUrl = runtime?.websiteUrl || 'https://pfandpirat.com/';
  const impressumUrl = runtime?.impressumUrl || '/impressum/';
  const detailedDonateUrl = '#/donate';

  const accountPromo = standalone ? (
    <div className="quick-mark-account-promo">
      <div>
        <strong>Selbst Funde sammeln?</strong>
        <span>Mit Account siehst du die Schatzkarte, speicherst Abholungen, sammelst XP und schaltest Achievements frei.</span>
      </div>
      <a className="quick-mark-account-button" href={appUrl}>Zur App-Startseite</a>
    </div>
  ) : null;

  const [step, setStep] = useState('location');
  const [location, setLocation] = useState(null);
  const [error, setError] = useState('');
  const [submittingKey, setSubmittingKey] = useState('');
  const [result, setResult] = useState(null);
  const [localToastChange, setLocalToastChange] = useState(null);
  const [showLocalToast, setShowLocalToast] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [activeEvent, setActiveEvent] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(() => (typeof window !== 'undefined' ? window.pfpQuickMarkInstallPrompt || null : null));
  const [installMode, setInstallMode] = useState(() => (isStandaloneDisplay() ? 'installed' : 'checking'));
  const [showInstallHint, setShowInstallHint] = useState(false);
  const [shareState, setShareState] = useState({ busy: '', message: '', type: '' });
  const sharingFlags = useMemo(() => getFundSharingFlags(runtime), [runtime]);

  useEffect(() => () => {
    if (localToastTimerRef.current) {
      window.clearTimeout(localToastTimerRef.current);
    }
  }, []);

  useEffect(() => () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
  }, [photoPreview]);

  useEffect(() => {
    if (isStandaloneDisplay()) {
      setInstallMode('installed');
      return undefined;
    }

    const applyPrompt = (event) => {
      event?.preventDefault?.();
      const prompt = event?.prompt ? event : window.pfpQuickMarkInstallPrompt;
      if (!prompt?.prompt) return;
      setInstallPrompt(prompt);
      setInstallMode('available');
      setShowInstallHint(false);
    };

    if (window.pfpQuickMarkInstallPrompt?.prompt) {
      applyPrompt(window.pfpQuickMarkInstallPrompt);
    }

    const fallbackTimer = window.setTimeout(() => {
      setInstallMode((current) => (current === 'checking' ? 'manual' : current));
    }, 1400);

    const handleInstalled = () => {
      setInstallMode('installed');
      setInstallPrompt(null);
      setShowInstallHint(false);
    };

    window.addEventListener('beforeinstallprompt', applyPrompt);
    window.addEventListener('pfpquickmarkinstallavailable', applyPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('beforeinstallprompt', applyPrompt);
      window.removeEventListener('pfpquickmarkinstallavailable', applyPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt?.prompt) {
      try {
        await installPrompt.prompt();
        const choice = await installPrompt.userChoice;
        window.pfpQuickMarkInstallPrompt = null;
        setInstallPrompt(null);

        if (choice?.outcome === 'accepted') {
          setInstallMode('installed');
          setShowInstallHint(false);
          return;
        }
      } catch (err) {
        // Fall back to manual browser instructions.
      }
    }

    setInstallMode('manual');
    setShowInstallHint(true);
  };

  const showXpToastFromPayload = (payload) => {
    const toast = payload?.xp_toast && typeof payload.xp_toast === 'object' ? payload.xp_toast : null;
    if (!toast) return;

    const challengeFocusEnabled = isChallengeFocusEnabled();

    if (typeof gamification?.applyGamificationPayload === 'function') {
      gamification.applyGamificationPayload(payload, {
        emitXpToast: !challengeFocusEnabled,
        skipPresentations: challengeFocusEnabled,
        event: toast.event || payload.event || 'mark_pfandspende',
        awarded: toast.awarded ?? payload.awarded ?? 0,
        toastLabel: toast.label || '',
      });
      return;
    }

    if (challengeFocusEnabled) {
      return;
    }

    const nextChange = {
      id: Date.now() + Math.random(),
      event: toast.event || payload.event || 'mark_pfandspende',
      awarded: Number(toast.awarded ?? payload.awarded ?? 0),
      xp: Number(payload.xp || 0),
      label: toast.label || '',
      amount_type: toast.amount_type || payload.amount_type || '',
      amount_group: toast.amount_group || payload.amount_group || '',
      item_type: toast.item_type || payload.item_type || '',
      post_id: toast.post_id || payload.id || null,
      preview: Boolean(toast.preview || payload.preview),
      eligible: toast.eligible ?? payload.eligible,
      account_hint: toast.account_hint || '',
    };

    setLocalToastChange(nextChange);
    setShowLocalToast(false);
    window.requestAnimationFrame(() => setShowLocalToast(true));

    if (localToastTimerRef.current) {
      window.clearTimeout(localToastTimerRef.current);
    }

    localToastTimerRef.current = window.setTimeout(() => {
      setShowLocalToast(false);
      setLocalToastChange(null);
      localToastTimerRef.current = null;
    }, 2600);
  };

  const requestLocation = async () => {
    setError('');
    try {
      const nextLocation = await getPosition();
      setLocation(nextLocation);
      fetchActiveEvent(nextLocation)
        .then((payload) => setActiveEvent(payload?.event || null))
        .catch(() => setActiveEvent(null));
      setStep('type');
    } catch (err) {
      setError(err?.message || 'Der Standort konnte nicht ermittelt werden.');
    }
  };

  const clearPhotoSelection = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview('');
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    clearPhotoSelection();

    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      setError('Bitte wähle ein Bild aus.');
      event.target.value = '';
      return;
    }

    setError('');
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleOptionSelect = (option) => {
    if (!location?.latitude || !location?.longitude) {
      setError('Bitte gib zuerst deinen Standort frei.');
      setStep('location');
      return;
    }

    setError('');
    setSelectedOption(option);
    clearPhotoSelection();
    setStep('photo');
  };

  const submitMark = async (option = selectedOption, file = photoFile) => {
    if (!option) {
      setError('Bitte wähle zuerst aus, was dort bereitsteht.');
      setStep('type');
      return;
    }

    if (!location?.latitude || !location?.longitude) {
      setError('Bitte gib zuerst deinen Standort frei.');
      setStep('location');
      return;
    }

    setError('');
    setSubmittingKey(option.key);

    try {
      const formData = new FormData();
      formData.append('latitude', String(location.latitude));
      formData.append('longitude', String(location.longitude));
      formData.append('accuracy', location.accuracy === null || location.accuracy === undefined ? '' : String(location.accuracy));
      formData.append('pfand_type', option.pfandType);
      formData.append('item_type', option.itemType);
      formData.append('amount_type', option.amountType);
      formData.append('source', source);
      formData.append('website', '');
      if (activeEvent?.id) {
        formData.append('event_id', String(activeEvent.id));
      }
      if (selectedDrink?.id) {
        formData.append('drink_id', String(selectedDrink.id));
        formData.append('drink_ean', String(selectedDrink.ean || ''));
      }

      if (file) {
        formData.append('photo', await prepareImageUpload(file));
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
        },
        body: formData,
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || 'Die Markierung konnte nicht gespeichert werden.');
      }

      setResult(payload || null);
      if (payload?.drink_discovery) {
        emitDrinkDiscoveryChanges(payload.drink_discovery);
      }
      setShareState({ busy: '', message: '', type: '' });
      showXpToastFromPayload(payload || {});
      setStep('success');
    } catch (err) {
      setError(err?.message || 'Die Markierung konnte nicht gespeichert werden.');
      setStep('photo');
    } finally {
      setSubmittingKey('');
    }
  };

  const resetFlow = () => {
    setError('');
    setResult(null);
    setSubmittingKey('');
    setLocation(null);
    setActiveEvent(null);
    setSelectedOption(null);
    setShareState({ busy: '', message: '', type: '' });
    clearPhotoSelection();
    setStep('location');
  };

  const shellClassName = standalone ? 'quick-mark-shell quick-mark-shell--standalone' : 'quick-mark-shell';
  const isDuplicate = Boolean(result?.duplicate);
  const isGiveawayResult = result?.item_type === 'giveaway' || result?.pfand_type === 'zu_verschenken';
  const isGiveawaySelection = selectedOption?.itemType === 'giveaway' || selectedOption?.pfandType === 'zu_verschenken';
  const successTitle = isDuplicate ? 'Fundort wurde ergänzt' : (isGiveawayResult ? 'Fund ist markiert' : 'Pfand ist markiert');
  const successIcon = isDuplicate ? '↻' : '✓';
  const statusUrl = result?.status_url || result?.statusUrl || result?.status_fallback_url || '';
  const accountCta = result?.account_cta || {};
  const accountCtaUrl = accountCta.url || appUrl;
  const accountCtaLabel = accountCta.label || 'Account erstellen und Push freischalten';
  const notificationHint = result?.notification_hint || 'Speichere den Status-Link. Mit Account bekommst du beim naechsten Mal Push und XP.';
  const resultChallengeEvent = result?.challenge_event || (result?.event && typeof result.event === 'object' ? result.event : null);
  const sharePayload = useMemo(
    () => (result ? buildFundSharePayload(result, { ...runtime, appUrl }) : null),
    [result, runtime, appUrl]
  );

  const showInstallPanel = installMode !== 'installed';
  const installButtonLabel = installMode === 'available' ? 'Spende melden als Icon speichern' : 'Icon speichern';

  const copyStatusUrl = async () => {
    if (!statusUrl || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(statusUrl);
    } catch (err) {
      // The open button remains the fallback when clipboard access is blocked.
    }
  };

  const handleFundShare = async (channel) => {
    if (!sharePayload) return;
    setShareState({ busy: channel, message: '', type: '' });
    try {
      const response = await shareFund(channel, sharePayload);
      if (channel === 'copy' || response?.mode === 'clipboard') {
        setShareState({ busy: '', message: 'Link zur Beute wurde kopiert.', type: 'success' });
        return;
      }
      if (response?.mode === 'manual') {
        setShareState({ busy: '', message: response.message || 'Teilen hat nicht geklappt. Du kannst den Link stattdessen kopieren.', type: 'error' });
        return;
      }
      setShareState({ busy: '', message: '', type: '' });
    } catch (err) {
      setShareState({ busy: '', message: 'Teilen hat nicht geklappt. Du kannst den Link stattdessen kopieren.', type: 'error' });
    }
  };

  const skipSharing = () => {
    setShareState({ busy: '', message: 'Alles klar. Du kannst direkt weitermachen.', type: 'success' });
  };

  return (
    <div className={shellClassName}>
      <div className="quick-mark-card">
        <div className="quick-mark-hero">
          <div className="quick-mark-hero-top">
            <a className="quick-mark-logo-link" href={websiteUrl} aria-label="Zur Pfandpirat Website">
              <img src={logoUrl} alt="Pfandpirat" className="quick-mark-logo" />
            </a>
            <div className="quick-mark-hero-copy">
              <h1 className="quick-mark-title">
                {standalone ? 'Pfand teilen statt liegen lassen.' : 'Fund schnell auf der Karte markieren'}
              </h1>
              <p className="quick-mark-subtitle">
                Du hast Pfand oder Dinge zum Mitnehmen gesehen? Gib kurz den Standort frei,
                wähle die passende Kategorie aus und fertig. Andere sehen den Fund anschließend auf der Schatzkarte.
              </p>
            </div>
          </div>

          <div className="quick-mark-meta-row">
            <div className="quick-mark-meta-pill">
              <span className="quick-mark-meta-pill__icon">⌖</span>
              Quelle: {getSourceLabel(source)}
            </div>
            <div className="quick-mark-meta-pill">
              <span className="quick-mark-meta-pill__icon">✓</span>
              ohne Konto markieren
            </div>
            <div className="quick-mark-meta-pill">
              <span className="quick-mark-meta-pill__icon">♻</span>
              direkt auf der Schatzkarte sichtbar
            </div>
          </div>

          {selectedDrink ? (
            <div className="quick-mark-info-panel">
              <div className="quick-mark-info-icon">🥤</div>
              <div><strong>Für dein Logbuch ausgewählt</strong><div>{selectedDrink.name}</div></div>
            </div>
          ) : null}

          {showInstallPanel ? (
            <div className={`quick-mark-install-panel ${showInstallHint ? 'is-open' : ''}`}>
              <div>
                <strong>Direktzugriff aufs Handy legen</strong>
                <span>Speichere diese Seite als eigenes Icon mit dem Namen "Spende melden".</span>
                {showInstallHint || installMode === 'manual' ? (
                  <small>{getInstallHint()}</small>
                ) : null}
              </div>
              <button type="button" className="quick-mark-install-button" onClick={handleInstallClick}>
                {installButtonLabel}
              </button>
            </div>
          ) : null}
        </div>

        {step === 'location' && (
          <div className="quick-mark-section">
            <div className="quick-mark-step-label">Schritt 1 von 3</div>
            <h2>Fundort freigeben</h2>
            <p>
              Der Standort wird nur für diese eine Meldung gespeichert. Die App verfolgt dich nicht dauerhaft.
            </p>

            <div className="quick-mark-info-panel">
              <div className="quick-mark-info-icon">⌖</div>
              <div>
                <strong>Warum Standort?</strong>
                <div>Nur mit genauer Position können andere den Fund auf der Karte finden und mitnehmen.</div>
              </div>
            </div>

            <button type="button" className="quick-mark-button quick-mark-button--location" onClick={requestLocation}>
              Standort freigeben und Fund markieren
            </button>
            <div className="quick-mark-help">Danach wählst du nur noch aus, was dort bereitsteht.</div>
            {accountPromo}
          </div>
        )}

        {step === 'type' && (
          <div className="quick-mark-section">
            <div className="quick-mark-step-label">Schritt 2 von 3</div>
            <h2>Was steht bereit?</h2>
            <p>
              Wähle die passendste Option. Danach kannst du noch ein Foto ergänzen oder die Meldung direkt speichern.
            </p>

            <div className="quick-mark-location-box">
              <div className="quick-mark-location-box__title">Standort erkannt</div>
              <div className="quick-mark-location-coordinates">
                Latitude {Number(location?.latitude || 0).toFixed(6)} · Longitude {Number(location?.longitude || 0).toFixed(6)}
              </div>
              {location?.accuracy ? (
                <div className="quick-mark-location-accuracy">GPS-Genauigkeit ca. {Math.round(Number(location.accuracy))} m</div>
              ) : null}
            </div>

            {activeEvent ? (
              <div className="quick-mark-event-box">
                <strong>{activeEvent.title}</strong>
                <span>Diese Meldung zaehlt automatisch fuer die aktive Event-Challenge.</span>
              </div>
            ) : null}

            <div className="quick-mark-type-grid quick-mark-type-grid--options">
              {MARK_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`quick-mark-type-button ${option.className}`}
                  onClick={() => handleOptionSelect(option)}
                  disabled={Boolean(submittingKey)}
                >
                  <span className="quick-mark-type-image-wrap" aria-hidden="true">
                    <img className="quick-mark-type-image" src={option.image} alt="" />
                  </span>
                  <span className="quick-mark-type-title">{option.title}</span>
                  <span className="quick-mark-type-text">{option.text}</span>
                  {submittingKey === option.key ? <span className="quick-mark-inline-status">Wird gespeichert …</span> : null}
                </button>
              ))}
            </div>

            <div className="quick-mark-actions quick-mark-actions--compact">
              <button type="button" className="quick-mark-button quick-mark-button--ghost" onClick={() => setStep('location')}>
                Standort neu holen
              </button>
            </div>
          </div>
        )}

        {step === 'photo' && selectedOption && (
          <div className="quick-mark-section">
            <div className="quick-mark-step-label">Schritt 3 von 3</div>
            <h2>{isGiveawaySelection ? 'Foto empfohlen' : 'Foto optional'}</h2>
            <p>
              {isGiveawaySelection
                ? 'Ein Foto zeigt sofort, was dort kostenlos mitgenommen werden kann. Das macht die Meldung viel hilfreicher.'
                : 'Ein Foto hilft anderen, den Fund schneller zu erkennen. Du kannst die Meldung aber auch ohne Foto speichern.'}
            </p>

            <div className="quick-mark-location-box">
              <div className="quick-mark-location-box__title">{selectedOption.title}</div>
              <div>{selectedOption.text}</div>
            </div>

            {activeEvent ? (
              <div className="quick-mark-event-box">
                <strong>{activeEvent.title}</strong>
                <span>Foto und Standort helfen bei der Challenge-Validierung.</span>
              </div>
            ) : null}

            {error ? <div className="quick-mark-error quick-mark-error--inline">{error}</div> : null}

            <label className={`quick-mark-photo-drop ${photoPreview ? 'quick-mark-photo-drop--has-preview' : ''}`}>
              <input type="file" accept="image/*" onChange={handlePhotoChange} />
              {photoPreview ? (
                <img src={photoPreview} alt="Foto-Vorschau" className="quick-mark-photo-preview" />
              ) : (
                <span className="quick-mark-photo-placeholder">
                  <span className="quick-mark-photo-placeholder__icon" aria-hidden="true">📷</span>
                  <span>{isGiveawaySelection ? 'Foto machen oder auswählen' : 'Optional Foto hinzufügen'}</span>
                </span>
              )}
            </label>

            <button
              type="button"
              className="quick-mark-button quick-mark-button--location"
              onClick={() => submitMark(selectedOption, photoFile)}
              disabled={Boolean(submittingKey)}
            >
              {submittingKey ? 'Wird gespeichert …' : (photoFile ? 'Fund mit Foto markieren' : 'Ohne Foto markieren')}
            </button>

            <div className="quick-mark-actions quick-mark-actions--compact">
              {photoFile ? (
                <button
                  type="button"
                  className="quick-mark-button quick-mark-button--ghost"
                  onClick={() => submitMark(selectedOption, null)}
                  disabled={Boolean(submittingKey)}
                >
                  Ohne Foto markieren
                </button>
              ) : null}
              <button
                type="button"
                className="quick-mark-button quick-mark-button--ghost"
                onClick={() => {
                  clearPhotoSelection();
                  setStep('type');
                }}
                disabled={Boolean(submittingKey)}
              >
                Auswahl ändern
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="quick-mark-section quick-mark-section--success">
            <div className={`quick-mark-success-icon ${isDuplicate ? 'quick-mark-success-icon--duplicate' : ''}`}>{successIcon}</div>
            <div className="quick-mark-step-label">Ergebnis</div>
            <h2>{successTitle}</h2>
            <p>
              {result?.message || 'Die Markierung wurde gespeichert und kann jetzt Sammlern auf der Karte angezeigt werden.'}
            </p>

            <div className="quick-mark-success-box">
              <div><strong>{isGiveawayResult ? 'Kategorie' : 'Pfandart'}:</strong> {result?.pfand_type_label || getTypeLabel(result?.pfand_type)}</div>
              <div><strong>{isGiveawayResult ? 'Art' : 'Menge'}:</strong> {result?.amount_type_label || getAmountLabel(result?.amount_type)}</div>
              <div><strong>Quelle:</strong> {getSourceLabel(source)}</div>
              {result?.expires_at_label ? <div><strong>Sichtbar bis:</strong> {result.expires_at_label}</div> : null}
              {typeof result?.distance_meters !== 'undefined' && result?.distance_meters !== null ? (
                <div><strong>Abstand zur bestehenden Meldung:</strong> {result.distance_meters} m</div>
              ) : null}
              {typeof result?.confirmation_count !== 'undefined' && result?.confirmation_count !== null ? (
                <div><strong>Bestätigungen:</strong> {result.confirmation_count}</div>
              ) : null}
              {result?.confirmation_registered ? (
                <div><strong>Hinweis:</strong> Deine Sichtung wurde als Bestätigung gezählt.</div>
              ) : null}
            </div>

            {resultChallengeEvent ? (
              <div className="quick-mark-event-score">
                <strong>{resultChallengeEvent.title}</strong>
                <span>
                  {Number(result?.event_score?.points || 0) > 0
                    ? `+${Number(result.event_score.points)} Challenge-Punkte`
                    : 'Challenge-Zuordnung gespeichert'}
                </span>
              </div>
            ) : null}

            <FundShareCard
              payload={sharePayload}
              flags={sharingFlags}
              onShare={handleFundShare}
              shareState={shareState}
              onSkip={skipSharing}
            />

            {statusUrl ? (
              <div className="quick-mark-status-link">
                <div>
                  <strong>Benachrichtige mich, wenn es abgeholt wurde</strong>
                  <span>{notificationHint}</span>
                </div>
                <a className="quick-mark-status-link__url" href={statusUrl}>
                  Status-Link öffnen
                </a>
                <button type="button" className="quick-mark-button quick-mark-button--ghost quick-mark-status-link__copy" onClick={copyStatusUrl}>
                  Status-Link kopieren
                </button>
                <a className="quick-mark-account-button" href={accountCtaUrl}>
                  {accountCtaLabel}
                </a>
              </div>
            ) : null}

            <div className="quick-mark-actions">
              <button type="button" className="quick-mark-button quick-mark-button--location" onClick={resetFlow}>
                Noch einen Fund markieren
              </button>

              {standalone ? (
                <>
                  <a className="quick-mark-button quick-mark-button--secondary" href={appUrl}>
                    Zur App-Startseite
                  </a>
                  <a className="quick-mark-button quick-mark-button--ghost" href={websiteUrl}>
                    Zur Website
                  </a>
                </>
              ) : (
                <>
                  <a className="quick-mark-button quick-mark-button--secondary" href="#/map">
                    Zur Schatzkarte
                  </a>
                  <a className="quick-mark-button quick-mark-button--ghost" href={detailedDonateUrl}>
                    Erweiterte Meldung mit Details
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        {error && step !== 'photo' ? <div className="quick-mark-error">{error}</div> : null}
      </div>

      {standalone ? (
        <div className="quick-mark-footer">
          <a href={websiteUrl}>Zur Website</a>
          <a href={impressumUrl}>Impressum</a>
        </div>
      ) : null}

      <XpToast change={localToastChange} visible={showLocalToast} />
    </div>
  );
}

export default QuickMarkPage;
